import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { AppDevice, AppDeviceMessage, newAppDevice, newAppDeviceMessage } from "../AppBase";

interface AppDeviceFormProps {
  item: AppDevice;
  messages: AppDeviceMessage[];
  error?: string;
}

export interface AppDeviceFormRef {
  valid: () => boolean;
  getItem: () => AppDevice;
}

export const AppDeviceForm = forwardRef((props: AppDeviceFormProps, ref) => {
  const { error } = props;
  const [item, setItem] = useState<AppDevice>(newAppDevice());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof AppDevice)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  // Expose the valid function to the parent, and a method to get the whole item object
  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof AppDevice): string => {
    if (field === "name") {
      if (!item.name?.trim()) return "Navn kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseBlur = (field: keyof AppDevice) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  const handleIntegerChange = (field: keyof AppDevice, value: any) => {
    value = parseInt(value, 10);
    setItem((prev) => ({ ...prev, [field]: value }));
  }

  const handleBaseChange = (field: keyof AppDevice, value: any) => {  
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12} md={6}>
          <TextField
            label="Device"
            fullWidth
            value={item.device_id}
            onChange={(e) => handleBaseChange("device_id", e.target.value)}
            variant="outlined"
            error={validateBaseField("device_id") !== "" && (submitted || touched.base.device_id)}
            helperText={(submitted || touched.base.device_id) ? validateBaseField("device_id") : ""}
            onBlur={() => handleBaseBlur("device_id")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Company"
            fullWidth
            value={item.company_id ?? ''}
            onChange={(e) => handleIntegerChange("company_id", e.target.value)}
            variant="outlined"
            type={"number"}
            error={validateBaseField("company_id") !== "" && (submitted || touched.base.company_id)}
            helperText={(submitted || touched.base.company_id) ? validateBaseField("company_id") : ""}
            onBlur={() => handleBaseBlur("company_id")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Vessel"
            fullWidth
            value={item.vehicle_id}
            onChange={(e) => handleBaseChange("vehicle_id", e.target.value)}
            variant="outlined"
            error={validateBaseField("vehicle_id") !== "" && (submitted || touched.base.vehicle_id)}
            helperText={(submitted || touched.base.vehicle_id) ? validateBaseField("vehicle_id") : ""}
            onBlur={() => handleBaseBlur("vehicle_id")}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Initiated"
            required
            fullWidth
            value={item.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Last seen"
            required
            fullWidth
            value={item.last_seen_at}
            onChange={(e) => handleBaseChange("last_seen_at", e.target.value)}
            variant="outlined"
            error={validateBaseField("last_seen_at") !== "" && (submitted || touched.base.last_seen_at)}
            helperText={(submitted || touched.base.last_seen_at) ? validateBaseField("last_seen_at") : ""}
            onBlur={() => handleBaseBlur("last_seen_at")}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Messages"
            required
            fullWidth
            disabled
            value={""}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <TextField
            label="Action"
            required
            fullWidth
            value={item.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid> */}
      </Grid>
    </form>
  )
});